/** What recovery found for the stuck stream it tore down. */
export type RecoveryStreamOutcome =
  | 'completed'
  | 'failed'
  | 'timed_out'
  | 'never_started';

/** `failureReason` is the server note's `failure_reason` when it failed. */
export type RecoveryStreamResult = {
  outcome: RecoveryStreamOutcome;
  failureReason?: string;
};

/**
 * Tracks abort controllers aborted by Haiku SSE recovery (INT-5198) so the
 * stream abort handler stays silent — no "Generating stopped" toast and no
 * previous-note restore when we are tearing down a stuck stream to apply
 * server state.
 */
const abortedByRecoveryStream = new WeakMap<AbortController, RecoveryStreamResult>();

/**
 * Tracks abort controllers that were aborted because a *new* generation is
 * intentionally superseding the in-flight one (via
 * `cancelExistingStreamAndCreateNewAbortController`). This is distinct from an
 * explicit user-initiated stop (the Stop button aborts the controller
 * directly), so the stream's abort handler can stay silent — no "Generating
 * stopped" toast and no previous-note restore — when it's just being replaced.
 */
const supersededByNewStream = new WeakSet<AbortController>();

export const markAbortAsRecoveryStream = (
  controller: AbortController,
  result: RecoveryStreamResult
): void => {
  abortedByRecoveryStream.set(controller, result);
};

export const wasAbortedByRecoveryStream = (controller: AbortController): boolean =>
  abortedByRecoveryStream.has(controller);

export const getRecoveryStreamResult = (
  controller: AbortController
): RecoveryStreamResult | undefined => abortedByRecoveryStream.get(controller);

export const markAbortAsSupersedingStream = (controller: AbortController): void => {
  supersededByNewStream.add(controller);
};

/**
 * Whether the given controller was aborted by a newer generation taking over,
 * rather than by the user pressing Stop. Used by the stream abort handler to
 * suppress the "Generating stopped" toast in the supersede case.
 */
export const wasAbortedBySupersedingStream = (controller: AbortController): boolean =>
  supersededByNewStream.has(controller);
